import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { IPlaatoStatus } from 'app/models/plaato-status';
import { SignalRConnectionState } from 'app/models/plaato-message.model';
import * as PlaatoActions from './actions'

export interface State extends EntityState<IPlaatoStatus> {
  // additional entity state properties
  selectedId: string | null;
  error: string;
  connectionState: SignalRConnectionState;
  availableDevices: string[];
}

export function selectPlaatoId(a: IPlaatoStatus): string {
  //In this case this would be optional since primary key is id
  return a.id ?? '';
}
export const adapter: EntityAdapter<IPlaatoStatus> = createEntityAdapter<IPlaatoStatus>({
  selectId: selectPlaatoId
});

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  selectedId: null,
  error: '',
  connectionState: SignalRConnectionState.Disconnected,
  availableDevices: []
});

export const stateName = 'plaato-status';

export const reducer = createReducer<State>(
  initialState,

  on(PlaatoActions.upsertItem, (state, { item }) => {
    return adapter.upsertOne(item, state);
  }),

  on(PlaatoActions.deleteItem, (state, { id }) => {
    return adapter.removeOne(id, state);
  }),

  // Realtime Actions
  on(PlaatoActions.connectionStateChanged, (state, { state: connectionState }) => ({
    ...state,
    connectionState,
    error: (connectionState === SignalRConnectionState.Connected || connectionState === SignalRConnectionState.Connecting)
      ? ''
      : state.error
  })),

  on(PlaatoActions.deviceListReceived, (state, { devices }) => ({
    ...state,
    availableDevices: devices
  })),

  on(PlaatoActions.errorOccurred, (state, { error }) => ({
    ...state,
    error
  })),

  on(PlaatoActions.messageReceived, (state, { deviceId, message }) => {
    const existing = state.entities[deviceId];
    // Create partial update
    const update: Partial<IPlaatoStatus> = {
      latestMessage: message,
      // Optionally update base properties if they map directly
      temperature: message.temperature?.toString(),
      // amount: message.volume, // Depends on unit type mapping logic
    };

    // Append to history if we want to keep it in store (careful with size)
    if (existing?.history) {
      update.history = [...existing.history, message].slice(-50); // keep last 50
    } else {
      update.history = [message];
    }

    // If it exists, update it. If not, we might want to add it or ignore.
    // For now, let's assume we only update known devices or upsert if getting data.
    if (existing) {
      return adapter.updateOne({ id: deviceId, changes: update }, state);
    } else {
      // Create new minimal entry
      return adapter.addOne({
        id: deviceId,
        beverageName: 'Unknown',
        ...update
      } as IPlaatoStatus, state);
    }
  }),

  on(PlaatoActions.historyReceived, (state, { deviceId, history }) => {
    return adapter.upsertOne({
      id: deviceId,
      beverageName: state.entities[deviceId]?.beverageName ?? 'Unknown',
      history,
      latestMessage: history.length > 0 ? history[history.length - 1] : undefined
    } as IPlaatoStatus, state);
  }),

  on(PlaatoActions.subscribeToDevice, (state, { deviceId }) => {
    // Mark as subscribed - use upsertOne so entity is created if it doesn't exist yet
    const existing = state.entities[deviceId];
    if (existing) {
      return adapter.updateOne({ id: deviceId, changes: { isSubscribed: true } }, state);
    }
    return adapter.addOne({ id: deviceId, beverageName: 'Unknown', isSubscribed: true } as any, state);
  }),

  on(PlaatoActions.unsubscribeFromDevice, (state, { deviceId }) => {
    return adapter.updateOne({ id: deviceId, changes: { isSubscribed: false } }, state);
  })
);

// get the selectors
const {
  selectAll,
  selectEntities,
  selectIds,
  selectTotal
} = adapter.getSelectors();


// ***** SELECTORS *******
export const getSelectedId = (state: State) => state.selectedId;
export const getState = createFeatureSelector<State>(stateName);
export const getAllItems = createSelector(getState, selectAll);
export const getEntities = createSelector(getState, selectEntities);
export const getCurrentId = createSelector(getState, getSelectedId);
export const getCurrentItem = createSelector(getEntities, getCurrentId, (entities, id) => {
  if (id) return entities[id];
  return null;
});
export const getEntity = (id: string) => createSelector(getEntities, entities => entities[id]);

export const getConnectionState = createSelector(getState, state => state.connectionState);
export const getAvailableDevices = createSelector(getState, state => state.availableDevices);
export const getError = createSelector(getState, state => state.error);

